<script setup>
import { ref } from 'vue';
import { useBlockSafe } from '@/composables/useBlockSafe';
import CarTechnologyCard from '@/components/cards/CarTechnologyCard.vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';

import 'swiper/css/pagination';
import 'swiper/css/navigation';

import '@/assets/styles/swiper-custom.scss';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  items,
  hasTitle,
  hasItems,
  hasAnyContent,
} = useBlockSafe(props.data);
const isLocked = ref(true);
const onSwiper = (swiper) => {
  isLocked.value = swiper.isLocked;
};

const onLockToggle = (swiper) => {
  isLocked.value = swiper.isLocked;
};
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div
        class="car-technologies"
        :class="[backgroundColor === 'black' ? 'white' : '']"
      >
        <h2
          v-if="hasTitle"
          class="mb-0 car-technologies__title h2 text-center"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="title"
        ></h2>
        <div v-if="hasItems" class="car-technologies__slider">
          <Swiper
            :slides-per-view="'auto'"
            :space-between="16"
            :modules="[Navigation, Pagination]"
            :navigation="{
              prevEl: '.car-technologies__slider-navigation-prev',
              nextEl: '.car-technologies__slider-navigation-next',
            }"
            :pagination="{
              el: '.car-technologies__slider-pagination',
            }"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            @swiper="onSwiper"
            @lock="onLockToggle"
            @unlock="onLockToggle"
          >
            <SwiperSlide v-for="(item, index) in items" :key="index">
              <CarTechnologyCard
                :title="item?.title"
                :image="item?.image"
                :description="item?.description"
                :theme="backgroundColor"
              />
            </SwiperSlide>
          </Swiper>
          <div
            v-show="!isLocked"
            class="swiper-pagination swiper-pagination--onblack car-technologies__slider-pagination"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            :data-delay="items && items.length > 0 ? items.length * 100 : 100"
          ></div>
          <div
            v-show="!isLocked"
            class="swiper-navigation car-technologies__slider-navigation"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            :data-delay="items && items.length > 0 ? items.length * 100 : 100"
          >
            <Button
              type="button"
              rounded
              variant="outlined"
              class="car-technologies__slider-navigation-prev button-prev js-button-car-technologies-prev"
            >
              <CIcon
                class="button-prev__icon"
                :name="
                  backgroundColor === 'black'
                    ? 'direction-white'
                    : 'direction-black'
                "
                width="16"
                height="16"
              />
            </Button>
            <Button
              type="button"
              rounded
              variant="outlined"
              class="car-technologies__slider-navigation-next button-next js-button-car-technologies-next"
            >
              <CIcon
                class="button-next__icon"
                :name="
                  backgroundColor === 'black'
                    ? 'direction-white'
                    : 'direction-black'
                "
                width="16"
                height="16"
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use 'sass:math';
.car-technologies {
  display: flex;
  flex-direction: column;
  gap: 56px;
  @include media-breakpoint-up(sm) {
    gap: 80px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__title {
    margin: 0;
  }
  &__slider {
    .swiper {
      height: 100%;
      overflow: visible;
      width: 100%;
    }
    .swiper-slide {
      align-self: stretch;
      height: auto;
      max-width: calc(100vw - 48px);
      width: 312px;
      @include media-breakpoint-up(sm) {
        max-width: unset;
        width: 348px;
      }
      @include media-breakpoint-up(md) {
        width: 384px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(width, 384);
      }
    }
  }
}
</style>
